import { useCallback, useMemo } from "react";
import { DEFAULT_DATE_RANGE } from "../constants/automapDealerDashboardConstants";
import { DEFAULT_DEALER_REPORT_RANGE } from "../constants/automapDealerPerformanceConstants";
import { useLocale } from "../intl";
import { useCurrency } from "../intl/CurrencyProvider.jsx";
import {
  translateDealerAlert,
  translateDealerChecklistItem,
  translateDealerDashboardPayload,
  translateDealerApiError,
  translateDealerFeaturedPayload,
  translateDealerPlanPayload,
  translateDealerProfileHealthItem,
  translateDealerProfileHealthLabel,
  translateDealerTrialMessage,
} from "../utils/dealerPortalServerI18n";

const DATE_RANGE_VALUES = ["today", "last_7_days", "last_30_days", "this_month"];
const REPORT_DATE_RANGE_VALUES = ["last_7_days", "last_30_days", "this_month", "trial_period"];

function lookupEnum(t, prefix, value, fallback) {
  if (!value) return fallback ?? "—";
  const key = `${prefix}.${value}`;
  const translated = t(key);
  if (!translated || translated === key || translated.startsWith("[MISSING:")) {
    return fallback ?? value;
  }
  return translated;
}

export function useDealerPortalIntl() {
  const { t: fullT } = useLocale();
  const { formatMoney } = useCurrency();

  const dp = useCallback((key, params) => fullT(`automotive.dealerPortal.${key}`, params), [fullT]);

  const dateRangeOptions = useMemo(
    () => DATE_RANGE_VALUES.map((value) => ({ value, label: dp(`dateRanges.${value}`) })),
    [dp]
  );

  const reportDateRangeOptions = useMemo(
    () => REPORT_DATE_RANGE_VALUES.map((value) => ({ value, label: dp(`dateRanges.${value}`) })),
    [dp]
  );

  const formatLeadStatus = useCallback(
    (status) => lookupEnum(fullT, "automotive.dealerPortal.leadStatus", status),
    [fullT]
  );

  const formatLeadTemperature = useCallback(
    (value) => lookupEnum(fullT, "automotive.dealerPortal.leadTemperature", value),
    [fullT]
  );

  const formatLeadType = useCallback(
    (type) => lookupEnum(fullT, "automotive.dealerPortal.leadType", type),
    [fullT]
  );

  const formatLeadOutcome = useCallback(
    (value) => lookupEnum(fullT, "automotive.dealerPortal.leadOutcome", value),
    [fullT]
  );

  const formatLeadQuality = useCallback(
    (value) => lookupEnum(fullT, "automotive.dealerPortal.leadQuality", value),
    [fullT]
  );

  const formatLeadQualityTag = useCallback(
    (value) => lookupEnum(fullT, "automotive.dealerPortal.leadQualityTag", value),
    [fullT]
  );

  const formatListingStatus = useCallback(
    (status) => lookupEnum(fullT, "automotive.dealerPortal.listingStatus", status),
    [fullT]
  );

  const formatPlacementStatus = useCallback(
    (status) => lookupEnum(fullT, "automotive.dealerPortal.sponsorships.placementStatus", status),
    [fullT]
  );

  const formatLeadSortLabel = useCallback(
    (value) => lookupEnum(fullT, "automotive.dealerPortal.leadSort", value),
    [fullT]
  );

  const formatInventorySortLabel = useCallback(
    (value) => lookupEnum(fullT, "automotive.dealerPortal.inventorySort", value),
    [fullT]
  );

  const formatQuickActionLabel = useCallback(
    (action) => lookupEnum(fullT, "automotive.dealerPortal.leadQuickAction", action),
    [fullT]
  );

  const formatReportNavLabel = useCallback(
    (key) => dp(`reports.nav.${key}`),
    [dp]
  );

  const formatTrialReadiness = useCallback(
    (value) => lookupEnum(fullT, "automotive.dealerPortal.trialReadiness", value),
    [fullT]
  );

  const formatListingAction = useCallback(
    (value) => lookupEnum(fullT, "automotive.dealerPortal.listingAction", value),
    [fullT]
  );

  const formatPlanLabel = useCallback(
    (planKey, publicName) => {
      if (publicName) return publicName;
      return lookupEnum(fullT, "automotive.dealerPortal.plan.planLabels", planKey, planKey);
    },
    [fullT]
  );

  const formatPlanLimit = useCallback(
    (value) => (value == null ? dp("plan.unlimited") : String(value)),
    [dp]
  );

  const formatReportsAccess = useCallback(
    (value) => {
      if (value === false || value == null) return dp("plan.notIncluded");
      if (value === "basic") return dp("plan.reportsBasic");
      if (value === "full") return dp("plan.reportsFull");
      if (value === true) return dp("plan.featureYes");
      return String(value);
    },
    [dp]
  );

  const formatPlanFeature = useCallback(
    (value) => {
      if (value == null) return dp("plan.unlimited");
      if (value === true) return dp("plan.featureYes");
      if (value === false) return dp("plan.featureNo");
      if (value === "basic") return dp("plan.reportsBasic");
      if (value === "full") return dp("plan.reportsFull");
      if (typeof value === "number") return String(value);
      return String(value);
    },
    [dp]
  );

  const formatReadinessLabel = useCallback(
    (value) => lookupEnum(fullT, "automotive.dealerPortal.readiness", value),
    [fullT]
  );

  const formatPlanTrialStatus = useCallback(
    (status) => lookupEnum(fullT, "automotive.dealerPortal.plan.trialStatus", status),
    [fullT]
  );

  const formatPlanConversionStage = useCallback(
    (stage) => lookupEnum(fullT, "automotive.dealerPortal.plan.conversionStage", stage),
    [fullT]
  );

  const translateOnboardingChecklistItem = useCallback(
    (item) => {
      if (!item?.key) return item;
      return {
        ...item,
        title: lookupEnum(
          fullT,
          "automotive.dealerPortal.server.onboardingChecklist",
          item.key,
          item.title
        ),
      };
    },
    [fullT]
  );

  const translateError = useCallback(
    (message, context = "generic", code, status) =>
      translateDealerApiError(message, fullT, `automotive.dealerPortal.errors.${context}`, code, status),
    [fullT]
  );

  const localizeDashboard = useCallback(
    (dashboard) => translateDealerDashboardPayload(dashboard, fullT),
    [fullT]
  );

  const localizePlan = useCallback(
    (payload) => translateDealerPlanPayload(payload, fullT),
    [fullT]
  );

  const localizeFeatured = useCallback(
    (payload) => translateDealerFeaturedPayload(payload, fullT),
    [fullT]
  );

  return {
    t: dp,
    fullT,
    formatMoney,
    dateRangeOptions,
    reportDateRangeOptions,
    defaultDateRange: DEFAULT_DATE_RANGE,
    defaultReportRange: DEFAULT_DEALER_REPORT_RANGE,
    formatLeadStatus,
    formatLeadTemperature,
    formatLeadType,
    formatLeadOutcome,
    formatLeadQuality,
    formatLeadQualityTag,
    formatListingStatus,
    formatPlacementStatus,
    formatLeadSortLabel,
    formatInventorySortLabel,
    formatQuickActionLabel,
    formatReportNavLabel,
    formatTrialReadiness,
    formatListingAction,
    formatPlanLabel,
    formatPlanLimit,
    formatReportsAccess,
    formatPlanFeature,
    formatReadinessLabel,
    formatPlanTrialStatus,
    formatPlanConversionStage,
    translateOnboardingChecklistItem,
    translateError,
    localizeDashboard,
    localizePlan,
    localizeFeatured,
    translateChecklistItem: (item) => translateDealerChecklistItem(item, fullT),
    translateAlert: (alert) => translateDealerAlert(alert, fullT),
    translateProfileHealthItem: (item) => translateDealerProfileHealthItem(item, fullT),
    translateProfileHealthLabel: (score, label) =>
      translateDealerProfileHealthLabel(score, label, fullT),
    translateTrialMessage: (panel) => translateDealerTrialMessage(panel, fullT),
  };
}
